import { ExternalLink, FolderGit2 } from 'lucide-react';

import { GithubIcon } from '@/components/icons/GithubIcon';

import { cn } from '@/lib/utils';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { siteConfig } from '@/config/site';
import { SectionHeading } from '@/components/SectionHeading';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-500',
  maintained: 'bg-primary',
  experiment: 'bg-spark',
};

interface Project {
  name: string;
  description: string;
  tags: readonly string[];
  repo?: string;
  demo?: string;
  featured?: boolean;
  status?: string;
}

function ProjectCard({ project }: { project: Project }) {
  const repo = sanitizeUrl(project.repo);
  const demo = sanitizeUrl(project.demo);

  return (
    <article
      className={cn(
        'group relative flex flex-col gap-4 rounded-xl border bg-card p-6 transition-all duration-300',
        'hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-xl hover:shadow-primary/5 motion-reduce:hover:translate-y-0',
        project.featured && 'sm:col-span-2',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
          <FolderGit2 className="size-5" />
        </span>

        <div className="flex items-center gap-3">
          {project.status && (
            <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              <span className={cn('size-1.5 rounded-full', STATUS_STYLES[project.status] ?? 'bg-muted-foreground')} />
              {project.status}
            </span>
          )}
          {repo && (
            <a
              href={repo}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${project.name} repository`}
              className="text-muted-foreground transition-colors hover:text-primary"
            >
              <GithubIcon className="size-4" />
            </a>
          )}
          {demo && (
            <a
              href={demo}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${project.name} live demo`}
              className="text-muted-foreground transition-colors hover:text-primary"
            >
              <ExternalLink className="size-4" />
            </a>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="font-display text-xl font-semibold tracking-tight transition-colors group-hover:text-primary">
          {project.name}
        </h3>
        <p className="flex-1 text-sm leading-relaxed text-muted-foreground">{project.description}</p>
      </div>

      <div className="mt-auto flex flex-wrap gap-2 pt-1">
        {project.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border px-2.5 py-0.5 font-mono text-[11px] text-muted-foreground"
          >
            {tag}
          </span>
        ))}
      </div>
    </article>
  );
}

export function ProjectsSection() {
  return (
    <section id="work" className="scroll-mt-16 border-y bg-secondary/30">
      <div className="container py-20 md:py-28">
        <SectionHeading
          kicker="// selected work"
          title="Things I've built"
          sub="Everything is free and open source. Fork it, break it, ship it — that's the whole point."
        />

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {siteConfig.projects.map((project) => (
            <ProjectCard key={project.name} project={project} />
          ))}
        </div>

        {siteConfig.links.github && (
          <p className="mt-8 font-mono text-sm text-muted-foreground">
            More on{' '}
            <a
              href={siteConfig.links.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline-offset-4 hover:underline"
            >
              GitHub
            </a>{' '}
            — contributions always welcome.
          </p>
        )}
      </div>
    </section>
  );
}
